import { useMemo, useState } from 'react';
import { Lock, Search, ShieldAlert, X } from 'lucide-react';

import { useAdminTagsQuery } from '@/client/api/tags/tag.query';
import { CMSTag } from '@/client/types/cms';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type TagPickerProps = {
  value: number[];
  onChange: (tagids: number[]) => void;
  /** Entity type id for compatibility checks (etid_json). */
  etid?: number | null;
  disabled?: boolean;
  allowInactive?: boolean;
  className?: string;
  placeholder?: string;
  /**
   * Already-known tags (e.g. an entity's current tags) used to resolve selected
   * chip labels. Without this, a selected tag that isn't in the current search
   * page falls back to showing its raw `#id` instead of its name.
   */
  knownTags?: CMSTag[];
};

const isTruthy = (v: unknown) => v === true || v === 1 || v === '1';

const tagIncompatible = (tag: CMSTag, etid?: number | null) => {
  if (etid == null) return false;
  const allowed = tag.etid_json;
  if (!allowed || !Array.isArray(allowed) || allowed.length === 0) return false;
  return !allowed.map(Number).includes(Number(etid));
};

const TagChip = ({
  tag,
  onRemove,
  disabled,
}: {
  tag: CMSTag;
  onRemove?: () => void;
  disabled?: boolean;
}) => (
  <Badge
    variant="secondary"
    className={cn(
      'gap-1 pr-1 font-normal',
      !isTruthy(tag.active) && tag.active != null && 'opacity-60 line-through',
      isTruthy(tag.protected) && 'border border-amber-300 bg-amber-50 text-amber-900',
    )}
  >
    {isTruthy(tag.protected) && <Lock className="h-3 w-3" />}
    {tag.name}
    {!disabled && onRemove && (
      <button
        type="button"
        className="ml-0.5 rounded p-0.5 hover:bg-black/10"
        onClick={onRemove}
        aria-label={`Remove ${tag.name}`}
      >
        <X className="h-3 w-3" />
      </button>
    )}
  </Badge>
);

/**
 * Searchable multi-select for the global tag catalog.
 * Surfaces inactive, protected, anchor-only (in_use=0), autogenerated, and
 * entity-type-incompatible states instead of silently allowing invalid picks.
 */
export function TagPicker({
  value,
  onChange,
  etid,
  disabled,
  allowInactive = true,
  className,
  placeholder = 'Search tags…',
  knownTags,
}: TagPickerProps) {
  const [q, setQ] = useState('');
  const { data, isLoading } = useAdminTagsQuery(
    {
      q: q || undefined,
      limit: 40,
      include_inactive: allowInactive,
      include_usage: false,
    },
    !disabled,
  );

  const catalog = data?.tags ?? [];
  const byId = useMemo(() => {
    const map = new Map<number, CMSTag>();
    // Seed with caller-supplied known tags first so selected chips resolve to
    // names even when they're outside the current search page; the live catalog
    // then overrides with the freshest copy.
    (knownTags ?? []).forEach((t) => map.set(t.tagid, t));
    catalog.forEach((t) => map.set(t.tagid, t));
    return map;
  }, [catalog, knownTags]);

  const selected = value
    .map((id) => byId.get(id) ?? ({ tagid: id, name: `#${id}` } as CMSTag))
    .filter(Boolean);

  const toggle = (tag: CMSTag) => {
    if (disabled) return;
    const inactive = tag.active != null && !isTruthy(tag.active);
    const anchorOnly = tag.in_use != null && !isTruthy(tag.in_use);
    const incompatible = tagIncompatible(tag, etid);
    if (inactive || anchorOnly || incompatible) return;
    if (value.includes(tag.tagid)) {
      onChange(value.filter((id) => id !== tag.tagid));
    } else {
      onChange([...value, tag.tagid]);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((t) => (
            <TagChip
              key={t.tagid}
              tag={t}
              disabled={disabled}
              onRemove={() => onChange(value.filter((id) => id !== t.tagid))}
            />
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
        <Input
          className="pl-8 text-sm"
          placeholder={placeholder}
          value={q}
          disabled={disabled}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="max-h-48 overflow-y-auto rounded-md border bg-white">
        {isLoading && <p className="px-3 py-2 text-xs text-gray-500">Loading…</p>}
        {!isLoading && catalog.length === 0 && (
          <p className="px-3 py-2 text-xs text-gray-500">No tags match.</p>
        )}
        {catalog.map((t) => {
          const inactive = t.active != null && !isTruthy(t.active);
          const anchorOnly = t.in_use != null && !isTruthy(t.in_use);
          const incompatible = tagIncompatible(t, etid);
          const blocked = inactive || anchorOnly || incompatible;
          const selectedRow = value.includes(t.tagid);
          return (
            <button
              key={t.tagid}
              type="button"
              disabled={disabled || (blocked && !selectedRow)}
              onClick={() => toggle(t)}
              className={cn(
                'flex w-full items-start justify-between gap-2 px-3 py-1.5 text-left text-xs',
                selectedRow ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50',
                blocked && !selectedRow && 'cursor-not-allowed opacity-50',
              )}
            >
              <span>
                <span className="font-medium">{t.name}</span>
                <span className="ml-1 font-mono text-[10px] text-gray-400">#{t.tagid}</span>
                <span className="mt-0.5 flex flex-wrap gap-1">
                  {isTruthy(t.protected) && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700">
                      <Lock className="h-2.5 w-2.5" /> protected
                    </span>
                  )}
                  {inactive && (
                    <span className="text-[10px] text-amber-700">inactive</span>
                  )}
                  {anchorOnly && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500">
                      <ShieldAlert className="h-2.5 w-2.5" /> anchor only
                    </span>
                  )}
                  {isTruthy(t.autogen) && (
                    <span className="text-[10px] text-violet-600">autogen</span>
                  )}
                  {incompatible && (
                    <span className="text-[10px] text-red-600">wrong entity type</span>
                  )}
                </span>
              </span>
              {selectedRow && <span className="text-[10px] font-medium text-blue-700">Selected</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default TagPicker;
